import { Fragment, useEffect, useState } from 'react';
import { formatTime } from '../../../../core/commom/formatTime';
import { Typography } from '../../../../core/components/molecules/Typography';
import { usePodcast } from '../../hooks/usePodcasts';
import { usePodcastStore } from '../../stores/podcastsStore';
import { Colors, IPodcasts } from '../../../../core/models/disciplines';
import { getViewedFromResponse } from '../../../../core/models/contentView';
import { useNewSubjectStore } from '../../stores/newSubjectStore';
import CardLoader from '../cardLoading';
import ContentCard from '../../../../core/components/organism/ContentCard';
import SectionHeader from '../sectionHeader';
import PodcastPlayer from '../../../../core/components/organism/PodcastPlayer';
import { useFilterConfigStore } from '../../stores/filterConfig';
import FilterModal from '../ModalFilter';
import { IResponseFavorite } from '../../hooks/useContentFavorite';
import DisciplinesEmptyState from '../EmptyState';
import getSubjectByFilter from '../../../../core/commom/getSubjectByFilter';
import { useFinishMaterial } from '../../../../core/hooks/useFinishMaterial';
import { useAccess } from '../../../../core/accessControl/useAccess';
import { openFreemiumUpsell } from '../../../../core/stores/freemiumUpsellStore';

interface IPodcastList {
  module: string;
  discipline: Colors;
}

export default function PodcastList({ module, discipline }: IPodcastList) {
  const [playerState, setPlayerState] = useState({
    play: false,
    title: '',
    uri: ''
  });

  const [dataArray, setDataArray] = useState<IPodcasts[]>([]);
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);

  const { isContentLocked } = useAccess();

  const { mutate, isPending } = usePodcast();
  const { podcasts, setPodcasts } = usePodcastStore();
  const { selectedSubjects } = useNewSubjectStore();
  const { resetFilters, status, favorites } = useFilterConfigStore();
  const { mutate: finishMaterial } = useFinishMaterial();

  useEffect(() => {
    if (module) {
      mutate(module);
    }
  }, [module]);

  useEffect(() => {
    if (podcasts.length > 0) {
      filterData();
    }
  }, [podcasts, selectedSubjects, status, favorites]);

  const filterData = () => {
    let filteredData = [...podcasts];
    
    if (selectedSubjects.length > 0) {
      filteredData = filteredData.filter((it) => 
        selectedSubjects.includes(it.subjects[0].slug)
      );
    }

    if (status.length === 1) {
      const isNotListened = status.includes('not_listened');
      filteredData = filteredData.filter((it) => 
        isNotListened ? !it.done : it.done
      );
    }

    if (favorites) {
      filteredData = filteredData.filter((it) => it.favorite);
    }

    setDataArray(filteredData);
  };

  const playPodcast = (podcast: IPodcasts) => {
    setPlayerState({
      play: true,
      title: podcast.title,
      uri: podcast.uri
    });

    // "opened" é toggle: ao dar play só marca quando ainda não está visto, para
    // não desmarcar um podcast já concluído.
    if (!podcast.done) {
      onFinish(podcast.id, podcast.slug);
    }
  };

  const dispatchSetContentAsDoneEvent = (id: string) => {
    window.dispatchEvent(
      new CustomEvent('setContentAsDone', {
        detail: { contentId: id },
      })
    );
  };

  const callbackFav = (data: IResponseFavorite) => {
    if(data.ok && module) {
      mutate(module)
    }
  };

  if (isPending) {
    return (
      <Fragment>
        {Array.from({ length: 10 }, (_, index) => (
          <CardLoader key={index} />
        ))}
      </Fragment>
    );
  }

  const onFinish = (id: string, slug: string) => {
    if (!slug) return;

    // Toggle: reflete o `viewStatus.viewed` retornado e atualiza o store de
    // forma imutável para a mudança aparecer sem recarregar a página.
    setLoadingSlug(slug);
    finishMaterial(
      { slug, type: 'podcast', origin: 'free_browsing' },
      {
        onSuccess: (res) => {
          const viewed = getViewedFromResponse(res);

          const updatedPodcasts = podcasts.map((it) =>
            it.slug === slug ? { ...it, done: viewed } : it
          );
          setPodcasts(updatedPodcasts);

          if (viewed) dispatchSetContentAsDoneEvent(id);
        },
        onSettled: () => setLoadingSlug(null),
      }
    );
  };

  return (
    <div className={`flex flex-col gap-lg${playerState.play ? ' mb-48' : ''}`}>
      <FilterModal
        title="Filtrar podcasts"
        onSubmit={() => filterData()} 
        reset={() => resetFilters()} 
        modalName='normal-filter' 
        type="podcast"
      />
      {dataArray.length === 0 ? <DisciplinesEmptyState /> : (
        <Fragment>
          <SectionHeader hasTitle={false}>
            <Typography size='small'>
              {dataArray.length || 0}{' '}
              {dataArray.length <= 1 ? 'podcast' : 'podcasts'}
            </Typography>
          </SectionHeader>
          {dataArray.map((it, i) => {
            const locked = isContentLocked(it);
            return (
            <ContentCard
              color={discipline}
              key={i}
              title={it.title}
              done={it.done}
              firstViewedAt={it.firstViewedAt}
              favorite={it.favorite}
              icon='mic'
              content='podcast'
              action={() =>
                locked ? openFreemiumUpsell() : playPodcast(it)
              }
              subject={getSubjectByFilter(it.subjects, selectedSubjects)}
              hasCallback
              callback={(data) => callbackFav(data)}
              slug={it.slug}
              onDone={(id: string, slug: string) => onFinish(id, slug)}
              doneLoading={loadingSlug === it.slug}
              id={it.id}
              locked={locked}
            >
              <Typography
                size='small'
                className=' transition delay-100 ease-in-out group-hover:text-card-color-default'
              >
                {it.duration ? formatTime(it.duration) : ''}
              </Typography>
            </ContentCard>
            );
          })}
        </Fragment>
      )}
      {playerState.play && (
        <PodcastPlayer
          title={playerState.title}
          uri={playerState.uri}
          setPlayer={() => setPlayerState(prev => ({ ...prev, play: false }))}
        />
      )}
    </div>
  );
}
